import { useState, useEffect } from 'react';
import {
  Smartphone,
  Trash2,
  RefreshCw,
  ShieldCheck,
  AlertCircle,
  X,
  Clock,
  Hash,
  Monitor,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { devices, Device } from '../api/client';
import { cn } from '../lib/utils';

const PAGE_SIZE = 25;

function formatDeviceDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString();
}

function formatRelative(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}

export default function Devices() {
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [revoking, setRevoking] = useState<string | null>(null);

  const fetchDevices = async (pageNum = page) => {
    try {
      setLoading(true);
      const res = await devices.list({
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
      });
      setDeviceList(res.devices);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch devices';
      console.error('Failed to fetch devices:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [page]);

  const handleRevoke = async (device: Device) => {
    if (!confirm(`Revoke device ${device.deviceId.slice(0, 12)}...?\n\nThis will force the device to re-attest on next app launch.`)) {
      return;
    }

    try {
      setRevoking(device.deviceId);
      await devices.revoke(device.deviceId);
      await fetchDevices();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke device';
      console.error('Failed to revoke device:', err);
      setError(message);
    } finally {
      setRevoking(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && deviceList.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading devices...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Devices</h1>
          <p className="text-muted-foreground mt-1">
            Manage attested devices ({total} registered)
          </p>
        </div>
        <button
          onClick={() => fetchDevices()}
          disabled={loading}
          className="btn-secondary px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Devices Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Device
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Auth Method
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Environment
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Counter
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Registered
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Last Used
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {deviceList.map((device, index) => (
              <tr
                key={device.deviceId}
                className="animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 50}ms` }}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <code className="text-xs text-muted-foreground font-mono truncate block max-w-[200px]" title={device.deviceId}>
                        {device.deviceId.slice(0, 16)}...
                      </code>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm capitalize">{device.authMethod}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-muted-foreground" />
                    <span className={cn(
                      'px-2 py-0.5 text-xs font-medium rounded-md',
                      device.environment === 'production'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    )}>
                      {device.environment}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-mono">{device.counter}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span title={formatDeviceDate(device.createdAt)}>
                      {formatRelative(device.createdAt)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span title={formatDeviceDate(device.lastUsedAt)}>
                      {formatRelative(device.lastUsedAt)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button
                    onClick={() => handleRevoke(device)}
                    disabled={revoking === device.deviceId}
                    className={cn(
                      'p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors',
                      revoking === device.deviceId && 'opacity-50 cursor-not-allowed'
                    )}
                    title="Revoke device"
                  >
                    {revoking === device.deviceId ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {deviceList.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <Smartphone className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">No attested devices</p>
                      <p className="text-sm text-muted-foreground/70">
                        Devices will appear here once they complete App Attest verification
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between animate-fade-in-up animate-delay-200">
          <p className="text-sm text-muted-foreground">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page >= totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
